// Active Users WebSocket service for real-time user tracking - shared singleton
import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';

interface ActiveUser {
  username: string;
  joined_at: string;
}

interface RoomActiveUsers {
  userCount: number;
  users: { [userId: string]: ActiveUser };
}

interface ActiveUsersByRoom {
  [roomId: string]: RoomActiveUsers;
}

interface ActiveUsersMessage {
  type: string;
  data: {
    activeUsersByRoom: ActiveUsersByRoom;
    changedRoom?: string;
    action?: string;
    userId?: string;
    username?: string;
  };
  timestamp: string;
}

class ActiveUsersWebSocketService {
  private static instance: ActiveUsersWebSocketService | null = null;
  private ws: WebSocket | null = null;
  private isConnecting: boolean = false;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 1000; // Start with 1 second
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private messageHandlers: ((message: ActiveUsersMessage) => void)[] = [];
  private isAuthenticated = false; // Flag to track authentication status
  private userStore: any = null; // Store reference to UserStore instance
  private environment = import.meta.env.VITE_ENVIRONMENT;

  // Observable state
  isConnected: boolean = false;
  activeUsersByRoom: ActiveUsersByRoom = {};
  lastMessage: ActiveUsersMessage | null = null;
  connectionError: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  static getInstance(): ActiveUsersWebSocketService {
    if (!ActiveUsersWebSocketService.instance) {
      ActiveUsersWebSocketService.instance = new ActiveUsersWebSocketService();
    }
    return ActiveUsersWebSocketService.instance;
  }

  // Method to set the UserStore instance
  setUserStore(userStore: any): void {
    this.userStore = userStore;
  }

  private getWebSocketUrl(): string {
    let backendUrl;
    
    if (import.meta.env.VITE_MODE === 'dev') {
      backendUrl = import.meta.env.VITE_BACKEND_SOCKET_ENDPOINT;
    } else {
      backendUrl = (window as any).config?.backendSocketEndpoint || (window as any).config?.backendBaseUrl;
    }
    
    // Fallback to localhost:8000 if no backend URL is configured
    if (!backendUrl) {
      backendUrl = 'http://localhost:8000';
    }
    
    if (backendUrl?.endsWith('/')) {
      backendUrl = backendUrl.slice(0, -1);
    }
    
    const wsUrl = backendUrl?.replace('https://', 'wss://').replace('http://', 'ws://');
    const fullUrl = `${wsUrl}/ws/active-users/`;
    
    return fullUrl;
  }

  connect(): WebSocket | null {
    // Check if we already have an active connection
    if (this.ws?.readyState === WebSocket.OPEN) {
      return this.ws;
    }

    // Check if we're already trying to connect
    if (this.isConnecting) {
      return null;
    }

    try {
      this.isConnecting = true;
      this.connectionError = null;
      this.isAuthenticated = false; // Reset authentication status
      
      this.ws = new WebSocket(this.getWebSocketUrl());

      this.ws.onopen = () => {
        runInAction(() => {
          this.isConnected = true;
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          this.reconnectDelay = 1000;
        });
        
        // Send "Connection Successful" message
        this.send({ type: 'connection_successful' });
        
        // Start heartbeat to keep connection alive during long periods
        this.startHeartbeat();
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          // Handle connection establishment message
          if (message.type === 'connection_established') {
            // Send user authentication details after connection is established
            this.sendUserAuthenticationDetails();
          }
          
          // Handle authentication response
          if (message.type === 'authentication_response') {
            if (message.authenticated) {
              this.isAuthenticated = true;
            } else {
              this.isAuthenticated = false;
              // Close connection if authentication fails
              this.disconnect();
            }
          }
          
          runInAction(() => {
            this.lastMessage = message;
          });
          
          this.messageHandlers.forEach(handler => {
            try {
              handler(message);
            } catch (error) {
              if (this.environment === 'development') {
                console.error('Error in Active Users WebSocket message handler:', error);
              }
              toast.error('Error in Active Users WebSocket message handler!');
            }
          });
        } catch (error) {
          if (this.environment === 'development') {
            console.error('Error parsing Active Users WebSocket message:', error);
          }
          toast.error('Error parsing Active Users WebSocket message!');
        }
      };

      this.ws.onerror = (error) => {
        runInAction(() => {
          this.isConnecting = false;
          this.connectionError = 'Connection error';
        });
      };

      this.ws.onclose = (event) => {
        runInAction(() => {
          this.isConnected = false;
          this.isConnecting = false;
        });
        
        this.isAuthenticated = false; // Reset authentication status
        
        // Stop heartbeat
        this.stopHeartbeat();
        
        // Only attempt reconnection if it wasn't a normal closure
        if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          
          setTimeout(() => {
            this.connect();
          }, this.reconnectDelay);
        }
        
        this.ws = null;
      };

      return this.ws;
    } catch (error) {
      runInAction(() => {
        this.isConnecting = false;
        this.connectionError = 'Connection failed';
      });
      return null;
    }
  }

  disconnect() {
    if (this.ws) {
      this.ws.close(1000); // Normal closure
      this.ws = null;
    }
    this.stopHeartbeat();
    this.isAuthenticated = false; // Reset authentication status
  }

  send(data: any): boolean {
    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify(data));
        return true;
      } catch (error) {
        return false;
      }
    } else {
      return false;
    }
  }

  get isUserAuthenticated(): boolean {
    return this.isAuthenticated;
  }

  /**
   * Send active user update with retry logic
   */
  sendActiveUserUpdate(data: any, maxRetries: number = 3): Promise<boolean> {
    return new Promise((resolve) => {
      let retryCount = 0;
      
      const attemptSend = () => {
        if (this.send(data)) {
          resolve(true);
          return;
        }
        
        retryCount++;
        if (retryCount < maxRetries) {
          setTimeout(attemptSend, 1000 * retryCount); // Exponential backoff
        } else {
          resolve(false);
        }
      };
      
      attemptSend();
    });
  }

  addMessageHandler(handler: (message: ActiveUsersMessage) => void) {
    this.messageHandlers.push(handler);
  }

  removeMessageHandler(handler: (message: ActiveUsersMessage) => void) {
    const index = this.messageHandlers.indexOf(handler);
    if (index > -1) {
      this.messageHandlers.splice(index, 1);
    }
  }

  private startHeartbeat() {
    this.stopHeartbeat(); // Clear any existing heartbeat
    this.heartbeatInterval = setInterval(() => {
      if (this.isConnected && this.isAuthenticated) {
        this.send({ type: 'heartbeat' });
      }
    }, 30000); // Send heartbeat every 30 seconds
  }

  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Send user authentication details to the server
   */
  private sendUserAuthenticationDetails(): void {
    // Check if we have access to UserStore
    if (!this.userStore) {
      this.send({ 
        type: 'authentication_failed', 
        message: 'UserStore not available' 
      });
      return;
    }

    // Check if user data is currently being loaded
    if (this.userStore.isUserInfoLoading) {
      // Retry authentication after 1 second
      setTimeout(() => {
        if (this.isConnected && !this.isAuthenticated) {
          this.sendUserAuthenticationDetails();
        }
      }, 1000);
      
      return;
    }

    // Check if user metadata is available
    if (!this.userStore.userMetadata) {
      // Retry authentication after 2 seconds
      setTimeout(() => {
        if (this.isConnected && !this.isAuthenticated) {
          this.sendUserAuthenticationDetails();
        }
      }, 2000);
      
      return;
    }

    try {
      const userInfo = this.userStore.userMetadata.userInfo;
      
      // Additional check to ensure userInfo exists
      if (!userInfo) {
        setTimeout(() => {
          if (this.isConnected && !this.isAuthenticated) {
            this.sendUserAuthenticationDetails();
          }
        }, 2000);
        
        return;
      }

      const userDetails = {
        type: 'user_authentication',
        user_id: userInfo?.id || '',
        email: userInfo?.email || '',
        name: userInfo?.name || '',
        department: userInfo?.deptId?.departmentCode || '',
        department_name: userInfo?.deptId?.departmentName || '',
        roles: this.userStore.userModulesRoles?.pps || [],
        timestamp: new Date().toISOString()
      };

      this.send(userDetails);
    } catch (error) {
      this.send({ 
        type: 'authentication_failed', 
        message: 'Error preparing user details' 
      });
    }
  }
}

// Export a singleton instance
export const activeUsersWebSocketService = ActiveUsersWebSocketService.getInstance();

// Export the class for type checking
export { ActiveUsersWebSocketService };
